import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectionStrategy,
  Input,
  OnChanges,
  SimpleChanges,
  ElementRef,
  Renderer2,
  PLATFORM_ID,
  inject,
  ChangeDetectorRef
} from '@angular/core';
import {
  MatCalendar,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { CommonModule, DatePipe, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Subject, combineLatest, takeUntil } from 'rxjs';
import { AvailabilityService } from './../../services/property-availability.service';
import { PropertyAvailability } from './../../models/property.model';

@Component({
  selector: 'app-property-calendar',
  templateUrl: './property-calendar.html',
  styleUrls: ['./property-calendar.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDatepickerModule, MatNativeDateModule, CommonModule],
  providers: [DatePipe],
})
export class PropertyCalendarComponent
  implements OnInit, OnChanges, OnDestroy {
  private availSrv = inject(AvailabilityService);
  private datePipe = inject(DatePipe);
  private route = inject(ActivatedRoute);
  private renderer = inject(Renderer2);
  private el = inject(ElementRef);

  @Input() propertyId!: string;
  @ViewChild(MatCalendar, { static: true }) calendar!: MatCalendar<Date>;
  @ViewChild('calendar') set calendarRef(calendar: MatCalendar<Date>) {
    if (calendar) {
      this.calendar = calendar;
      this.calendar.stateChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        setTimeout(() => this.injectPrices(), 50);
      });

      if (this.isReady) {
        this.calendar.updateTodaysDate();
        setTimeout(() => this.injectPrices(), 150);
      }
      this.cdr.markForCheck();
    }
  }

  platformId = inject(PLATFORM_ID);
  isBrowser = false;
  isReady = false;
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  today = new Date();
  minDate = new Date();
  maxDate = new Date();
  priceMap = new Map<string, number>();
  availMap = new Map<string, PropertyAvailability>();

  private refreshCalendar() {
    setTimeout(() => {
      this.calendar.updateTodaysDate();
      this.injectPrices();
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }, 100);
  }

  /* ------------------------------------------------------------------ *
   *  React to @Input or route param changes
   * ------------------------------------------------------------------ */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['propertyId'] && this.propertyId) {
      //console.log('propertyId', this.propertyId);
      this.loadProperty(this.propertyId);
    }
  }

  ngOnInit(): void {
    this.isReady = false;
    this.cdr.detectChanges();

    if (isPlatformBrowser(this.platformId)) {
      this.isBrowser = true;
    }

    this.setDates();
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const idFromRoute = params.get('id');
      if (idFromRoute && idFromRoute !== this.propertyId) {
        this.loadProperty(idFromRoute);
      }
    });

    if (this.propertyId) {
      //console.log('init', this.propertyId);
      this.loadProperty(this.propertyId);
    }

    // this.calendar.stateChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
    //   setTimeout(() => this.injectPrices(), 0);
    // });
  }

  // ngAfterViewChecked() {
  //   if (isPlatformBrowser(this.platformId)) {
  //     setTimeout(() => this.injectPrices());
  //     this.cdr.detectChanges();
  //   }
  // }

  setDates() {
    this.today.setHours(0, 0, 0, 0); // normalize to midnight

    // Optional: if you want to start selection from tomorrow
    this.minDate = new Date(this.today);
    this.minDate.setDate(this.today.getDate() + 1); // tomorrow

    // Limit to exactly 2 years in the future
    this.maxDate = new Date(this.today);
    this.maxDate.setMonth(this.today.getMonth() + 24);   // +23 months = Dec 2027 if today is Jan 2026
    this.maxDate.setDate(0);                             // set to last day of previous month → Dec 31, 2027
    this.maxDate.setHours(23, 59, 59, 999);              // end of day// end of day two years from now
    this.cdr.detectChanges();
  }

  private injectPrices() {
    if (!this.isBrowser) return;

    // 1. Find all cells
    const cells = this.el.nativeElement.querySelectorAll('.mat-calendar-body-cell');

    cells.forEach((cell: HTMLElement) => {
      // 2. IMMEDIATELY remove any existing price labels to prevent stacking
      const existingLabels = cell.querySelectorAll('.price-label');
      existingLabels.forEach(label => this.renderer.removeChild(cell, label));

      // 3. Extract the date from our custom class
      const dateClass = Array.from(cell.classList).find(c => c.startsWith('d-'));
      if (!dateClass) return;

      const isoDate = dateClass.substring(2);
      const priceCents = this.priceMap.get(isoDate);

      // 4. Inject the new label if price exists
      if (priceCents !== undefined) {
        const priceDiv = this.renderer.createElement('div');
        this.renderer.addClass(priceDiv, 'price-label');

        // Use standard textContent to avoid XSS/HTML issues
        const formattedPrice = `$${Math.round(priceCents / 100)}`;
        this.renderer.setProperty(priceDiv, 'textContent', formattedPrice);

        this.renderer.appendChild(cell, priceDiv);
      }
    });
    this.cdr.markForCheck();
  }

  /* ------------------------------------------------------------------ *
   *  Load data + subscribe (cancel old subs)
   * ------------------------------------------------------------------ */
  private loadProperty(id: string): void {
    // 1. Instantly clear the service state -> Emits empty map -> Hides calendar
  this.availSrv.setPropertyId(null);
  this.isReady = false; 
  this.cdr.detectChanges(); // Wipes the old calendar from DOM

  // 2. Set the new ID -> Triggers the API -> Emits fresh data
  this.availSrv.setPropertyId(id);

  this.destroy$.next(); 
  this.destroy$ = new Subject<void>();

  combineLatest([this.availSrv.priceMap$, this.availSrv.avail$])
    .pipe(takeUntil(this.destroy$))
    .subscribe(([priceMap, avail]) => {
      // Because of the 'null' clear above, this block WILL NOT FIRE 
      // with old data. It only fires when the new maps are completely ready.
      
      // We also check size > 0 to skip the empty emission we forced
      if (priceMap.size === 0 && avail.length === 0) return; 

      this.priceMap = priceMap;
      this.availMap = new Map(avail.map(a => [a.date, a]));
      this.isReady = true;

      if (this.calendar) {
        this.calendar.updateTodaysDate();
        setTimeout(() => this.injectPrices(), 150);
      }
      this.cdr.markForCheck();
    });
    // this.isReady = false;
    // this.priceMap = new Map();
    // this.availMap = new Map();
    // this.cdr.detectChanges();

    // this.availSrv.setPropertyId(id);

    // // Cancel previous subscriptions
    // // this.destroy$.next();
    // // this.destroy$.complete();
    // // this.destroy$ = new Subject<void>();
    // const currentDestroy$ = new Subject<void>();
    // this.destroy$.next(); // Trigger previous cleanup

    // combineLatest([this.availSrv.priceMap$, this.availSrv.avail$])
    //   .pipe(takeUntil(this.destroy$),
    // filter(([prices, avail]) => prices.size > 0 || this.isReady === false))
    //   .subscribe(([priceMap, avail]) => {
    //     this.priceMap = priceMap;
    //     this.availMap = new Map(avail.map(a => [a.date, a]));
    //     this.isReady = true;
    //     this.cdr.detectChanges();

    //     if (this.calendar) {
    //       // 1. Force the calendar to re-run dateClass/dateFilter
    //       this.calendar.updateTodaysDate();

    //       // 2. We use a slightly longer delay (100ms) or requestAnimationFrame 
    //       // to ensure the DOM is actually painted with the 'd-YYYY-MM-DD' classes.
    //       setTimeout(() => {
    //         this.injectPrices();
    //         this.isReady = true;
    //         this.cdr.markForCheck();
    //       }, 500);
    //     }

    //   });
  }

  private forceCalendarRefresh() {
    this.refreshTrigger++;
    this.cdr.markForCheck();

    if (!this.calendar) return;

    // Remember current view
    const originalActiveDate = this.calendar.activeDate;

    // Trick: move away & back (forces full re-render of cells)
    this.calendar.activeDate = new Date(originalActiveDate.getFullYear(), originalActiveDate.getMonth() + 1, 1);

    this.cdr.detectChanges(); // let it render the next month

    // Immediately go back
    this.calendar.activeDate = originalActiveDate;
    this.cdr.detectChanges();
    // Optional: also call these for good measure
    this.calendar.updateTodaysDate();
    this.injectPrices();
  }

  refreshTrigger = 0;


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ------------------------------------------------------------------ *
   *  Helpers
   * ------------------------------------------------------------------ */
  private iso(date: Date): string {
    return this.datePipe.transform(date, 'yyyy-MM-dd')!;
  }

  getPrice(date: Date): number {
    const iso = this.iso(date);
    const cents = this.priceMap.get(iso);
    if (cents === undefined) {
      //console.warn('No price for', iso);
      return 0;
    }
    return cents / 100;
  }

  isCheckinAllowed(date: Date): boolean {
    const entry = this.availMap.get(this.iso(date));
    return !entry || !!entry.checkinAllowed;
  }

  isBlocked(date: Date): boolean {
    const entry = this.availMap.get(this.iso(date));
    return !!entry && entry.status !== 'available';
  }

  /* ------------------------------------------------------------------ *
   *  CSS class per cell
   * ------------------------------------------------------------------ */
  // dateClass = (date: Date): string => {
  //   const iso = this.iso(date);
  //   const entry = this.availMap.get(iso);
  //   const hasPrice = this.priceMap.has(iso);

  //   if (!entry) {
  //     return hasPrice ? 'available' : 'available-no-price';
  //   }
  //   if (entry.status !== 'available') return 'blocked';
  //   if (!entry.checkinAllowed) return 'no-checkin';
  //   return hasPrice ? 'available' : 'available-no-price';
  // };

  /* ------------------------------------------------------------------ *
   *  Disable only dates that are explicitly NOT selectable
   * ------------------------------------------------------------------ */
  dateFilter = (date: Date | null): boolean => {
    if (!date) return false;
    const entry = this.availMap.get(this.iso(date));
    if (!entry) return true;                 // No record → allow
    return entry.status === 'available' && !!entry.checkinAllowed;
  };

  /* ------------------------------------------------------------------ *
   *  Selection
   * ------------------------------------------------------------------ */
  onSelect(date: Date | null): void {
    if (!date) return;
    const iso = this.iso(date);
    const entry = this.availMap.get(iso);

    if (entry && (entry.status !== 'available' || !entry.checkinAllowed)) {
      alert('This date is not available for check-in.');
      return;
    }

    const price = this.getPrice(date);
    //console.log('Selected', iso, 'Price $' + price);
  }

  /* ------------------------------------------------------------------ *
   * Updated dateClass: Adds a unique 'd-yyyy-mm-dd' class
   * ------------------------------------------------------------------ */
  dateClass = (date: Date): string => {
    const iso = this.iso(date);
    const entry = this.availMap.get(iso);

    // If data isn't loaded yet, return a loading class or default
    if (!this.isReady) return `d-${iso} loading`;

    let status = 'available';
    if (entry) {
      if (entry.status !== 'available') status = 'blocked';
      else if (!entry.checkinAllowed) status = 'no-checkin';
    }

    // Adding a "has-price" class can also help with CSS styling
    const priceClass = this.priceMap.has(iso) ? 'has-price' : 'no-price';

    return `${status} ${priceClass} d-${iso}`;
  };
}